import { AnswerStateEnum } from "@/common/composables/use-vocab-quiz";

/**
 * @typedef {{
 *  value: string,
 *  text: string,
 *  state: AnswerStateEnum,
 *  summary: int?,
 * }} AnswerButton
 */

/**
 * @typedef {{
 *  correctAnswer: string?,
 *  userAnswer: string?,
 *  state: AnswerStateEnum,
 * }} AnswerState
 */

/**
 * @typedef {{
 *  promptId: string,
 *  questions: Array<import("@/common/composables/use-vocab-quiz").Question>,
 *  userAnswers: Array<import("@/common/composables/use-vocab-quiz").UserAnswer>,
 *  quizState: number,
 *  answerValidator: Function,
 *  answerState: AnswerState?,
 *  explicitAnswerState: boolean,
 *  totalAnswerCount: int
 * }} Props
 */

/**
 * Function to watch state. Will trigger appropriate changes,
 * whenever question/answerState changes.
 *
 * This is useful for PENDING answers and then when receiving the correct answers,
 * we can show it.
 *
 * @param {import("@/common/composables/use-vocab-quiz").Question} question
 * @param {AnswerState} answerState
 * @param {Array<AnswerButton>} answerButtons
 * @param {Boolean} explicitAnswerState
 */
export const watchState = (question, answerState, answerButtons, explicitAnswerState) => {
  if (!question.options) {
    return;
  }

  if (answerState?.userAnswer) {
    for (const btn of answerButtons) {
      btn.summary = question.answerSummaries?.[btn.value];

      if (btn.value === answerState.userAnswer) {
        btn.state = answerState.state;
      } else if (explicitAnswerState) {
        btn.state =
          btn.value === answerState.correctAnswer
            ? AnswerStateEnum.Correct
            : AnswerStateEnum.Incorrect;
      }
    }
  }
};

/**
 * @param {import("@/common/composables/use-vocab-quiz").Question} question
 *
 * @return {AnswerButton}
 */
export const initializeAnswerButtons = (question) =>
  question.options.map((value) => ({
    value,
    text: value,
    state: AnswerStateEnum.Initial,
    summary: question.answerSummaries?.[value]
  }));

export const useGameComponent = () => {
  /** @type {Props} */
  const commonProps = {
    promptId: {
      type: String,
      required: true
    },
    questions: {
      type: Array,
      required: true
    },
    userAnswers: {
      type: Array,
      required: true
    },
    quizState: {
      type: Number,
      required: true
    },
    answerValidator: {
      type: Function,
      required: true
    },
    answerState: {
      type: Object,
      default: null
    },
    explicitAnswerState: {
      type: Boolean,
      default: false
    },
    totalAnswerCount: {
      type: Number,
      default: 0
    }
  };

  const commonEmits = ["answer", "enableQuiz"];

  return { commonProps, commonEmits };
};
